import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import dayjs from 'dayjs'
import { CircularProgress } from '@material-ui/core'
import SydModal from '../../../../../../commonDesign/SydModal'
import SydModalActions from '../../../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../../../commonDesign/Button'
import { useFinalizeClientStatusReport, useGetActiveClientStatusReport } from '../../../../../../../api/groups'
import SydLabel, { hookFormErrorAdapter } from '../../../../../../commonDesign/SydLabel'
import { useFormattingContext } from '../../../../../../organisms/FormattingProvider/FormattingContext'
import Divider from '../../../../../../atoms/Divider'
import SydDatePicker from '../../../../../../commonDesign/SydDatePicker'
import SydSwitch from '../../../../../../commonDesign/SydSwitch'
import ProblemDetailModal from '../ProblemDetailModal'
import ItemMigrationMapping from './ItemMigrationMapping'
import StatusTemplateSelect from './StatusTemplateSelect'

function FinalizeReportModal ({
  open,
  statusTemplateId,
  levelId,
  onClose,
  onSuccess
}) {
  // want to always load the current state
  const { data, isFetching } = useGetActiveClientStatusReport({
    includes: {
      items: true
    },
    filters: {
      statusTemplateId,
      levelId
    },
    format: 'list'
  }, {
    refetchOnMount: 'always',
    select: (data) => data?.statusReport
  })

  const form = useForm({
    defaultValues: {
      createNewReport: true,
      asOfDate: dayjs().add(-1, 'day').format('YYYY-MM-DD'),
      newTemplateId: null,
      itemMigrationMapping: {}
    }
  })

  // Track if we've initialized the form to avoid resetting user edits
  const hasInitialized = useRef(false)

  useEffect(() => {
    if (data && !hasInitialized.current) {
      form.reset({
        createNewReport: true,
        asOfDate: dayjs().add(-1, 'day').format('YYYY-MM-DD'),
        newTemplateId: data?.statusTemplateId,
        itemMigrationMapping: {}
      })
      hasInitialized.current = true
    }
  }, [data, form])

  // Reset initialization flag when modal closes
  useEffect(() => {
    if (!open) hasInitialized.current = false
  }, [open])

  const isCreatingNewReport = form.watch('createNewReport')

  const { formatter } = useFormattingContext()

  const { mutateAsync, isLoading } = useFinalizeClientStatusReport()

  const [submitProblemDetail, setSubmitProblemDetail] = useState(null)

  const onSubmit = async (formData) => {
    try {
      setSubmitProblemDetail(null)

      const itemMigrationMapping = formData.createNewReport
        ? Object.entries(formData.itemMigrationMapping).reduce((acc, [section, entries]) => {
          const sectionEntries = Object.entries(entries).reduce((subAcc, [oldCodeName, { enabled, matching }]) => {
            if (enabled && !!matching) {
              subAcc[oldCodeName] = matching
            }
            return subAcc
          }, {})

          if (Object.keys(sectionEntries ?? {}).length) {
            acc[section] = sectionEntries
          }
          return acc
        }, {})
        : undefined

      const result = await mutateAsync({
        statusReportId: data.statusReportId,
        levelId,
        statusTemplateId,
        createNewReport: formData.createNewReport,
        asOfDate: formData.createNewReport ? dayjs(formData.asOfDate).format('YYYY-MM-DD') : undefined,
        newTemplateId: formData.createNewReport ? formData.newTemplateId : undefined,
        itemMigrationMapping,
        force: !!formData.force
      })

      // Notify parent of success - use the newly created report ID if one was created
      if (onSuccess) {
        onSuccess({
          statusReportId: result?.newStatusReportId || data.statusReportId,
          clientId: levelId,
          asOfDate: formData.createNewReport ? formData.asOfDate : data.asOfDate
        })
      }

      onClose()
    } catch (error) {
      console.error('Failed to finalize status report:', error)

      if (error?.response) {
        setSubmitProblemDetail(error.response)
      }
    }
  }

  return (
    <>
      <SydModal
        open={open}
        onClose={onClose}
        title='Finalize Status Report'
        size='large'
      >
        <div className='flex flex-col' style={{ minHeight: '75vh' }}>
          {isFetching ? (
            <div className='flex items-center justify-center flex-1'>
              <CircularProgress />
            </div>
          ) : (
            <FormProvider {...form}>
              <form {...form} onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col flex-1'>
                <div className='flex-1 mb-4'>
                  <div className='flex gap-4 justify-between'>
                    <Controller
                      control={form.control}
                      name='createNewReport'
                      render={({ field, fieldState }) => (
                        <div className='flex gap-2 items-center'>
                          <SydSwitch
                            label='Create a new Report'
                            {...field}
                          />
                          <div className='flex-1 flex flex-col gap-4'>
                            <SydLabel
                              label='Create a new Report'
                              description='Create a new report as a part of this finalization'
                              error={hookFormErrorAdapter(form, fieldState)}
                            />
                            {!isCreatingNewReport && (
                              <div className='radius-md p-2 bg-neutral-300 font-light font-size-xs'>
                                By not creating a new report, the current report will be finalized and locked.
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    />
                  </div>

                  {isCreatingNewReport && (
                    <div className='flex flex-col gap-2'>
                      <Divider />

                      <div className='flex w-full gap-sm'>
                        <div className='flex-1'>
                          <StatusTemplateSelect />
                        </div>

                        <div>
                          <Controller
                            control={form.control}
                            name='asOfDate'
                            rules={{
                              required: {
                                value: isCreatingNewReport,
                                message: 'As of Date is required when creating a new report'
                              }
                            }}
                            render={({ field, fieldState }) => (
                              <SydLabel
                                label='As of Date'
                                description={
                                `Current report: ${formatter(data?.asOfDate, '@YYYY-MM-DD')}`
                                }
                                error={hookFormErrorAdapter(form, fieldState)}
                              >
                                <SydDatePicker
                                  {...field}
                                  size='sm'
                                  dateFormat='YYYY-MM-DD'
                                  disablePast={false}
                                />
                              </SydLabel>
                            )}
                          />
                        </div>
                      </div>

                      <Divider />

                      <SydLabel label='Choose the items to migrate to the new report'>
                        <ItemMigrationMapping statusReport={data} />
                      </SydLabel>
                    </div>
                  )}
                </div>

                <SydModalActions>
                  <SydButton
                    variant='secondary'
                    size='md'
                    onClick={onClose}
                    disabled={isLoading}
                  >
                    Cancel
                  </SydButton>

                  <SydButton
                    variant='primary'
                    size='md'
                    type='submit'
                    disabled={isLoading}
                    processing={isLoading}
                  >
                    {isLoading ? 'Saving...' : 'Finalize'}
                  </SydButton>
                </SydModalActions>
              </form>
            </FormProvider>
          )}
        </div>
      </SydModal>

      <ProblemDetailModal
        onClose={() => setSubmitProblemDetail(null)}
        onForce={async () => onSubmit({ ...form.getValues(), force: true })}
        problemDetail={submitProblemDetail}
      />
    </>
  )
}

FinalizeReportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  statusTemplateId: PropTypes.number.isRequired,
  levelId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess:
  PropTypes.func.isRequired
}

FinalizeReportModal.defaultProps = {
  currentAsOfDate: null
}

export default FinalizeReportModal
